/**
 * src/shell/i18n/services/DynamicI18nEngine.ts
 *
 * Runtime Internationalization & Dynamic Language Dictionary Engine.
 * - Hot-patches locale bundles at runtime without page reload.
 * - Hierarchical key-by-key fallback: Custom → Target → Base(en) → key
 * - Safe variable interpolation with HTML entity escaping (XSS-safe).
 * - Full translation workflow: Export base JSON, Template generation, 2-Column Editor,
 *   AST Key Parity Validation, and Ed25519-signed audit logging.
 */

import { auditLogger } from '../../../platform/observability/audit-logger';
import {
  LanguageCatalogEntry,
  TranslationValidationReport,
  TranslationEditRecord,
  TranslationFileExport,
} from '../types/i18n-workflow-types';

export interface LocalePack {
  locale: string;
  name: string;
  nativeName?: string;
  flagEmoji?: string;
  direction: 'ltr' | 'rtl';
  dictionary: Record<string, string>;
  /** True if this pack was user-uploaded vs. bundled */
  isCustom?: boolean;
}

export interface LocalePackMeta {
  locale: string;
  name: string;
  direction: 'ltr' | 'rtl';
  isCustom: boolean;
  keyCount: number;
  coveragePct: number;
}

const STORAGE_KEY_PACKS = 'sovereign_i18n_custom_packs_v1';
const STORAGE_KEY_ACTIVE = 'sovereign_i18n_active_locale_v1';

const HTML_ENTITY_MAP: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#x27;',
  '/': '&#x2F;',
};

function escapeHtml(str: string): string {
  return str.replace(/[&<>"'/]/g, (c) => HTML_ENTITY_MAP[c] ?? c);
}

/** Safe {name}-style interpolation with HTML escaping on values */
function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, key) => {
    const val = vars[key];
    if (val === undefined) return `{${key}}`;
    return escapeHtml(String(val));
  });
}

export interface CatalogDefinition {
  code: string;
  name: string;
  nativeName: string;
  flagEmoji: string;
  direction: 'ltr' | 'rtl';
  defaultDictionary?: Record<string, string>;
}

export const GLOBAL_LANGUAGE_CATALOG: CatalogDefinition[] = [
  { code: 'en', name: 'English (US)', nativeName: 'English (US)', flagEmoji: '🇺🇸', direction: 'ltr' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flagEmoji: '🇪🇸', direction: 'ltr' },
  { code: 'fr', name: 'French', nativeName: 'Français', flagEmoji: '🇫🇷', direction: 'ltr' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flagEmoji: '🇩🇪', direction: 'ltr' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', flagEmoji: '🇸🇦', direction: 'rtl' },
  { code: 'zh', name: 'Chinese (Simplified)', nativeName: '简体中文', flagEmoji: '🇨🇳', direction: 'ltr' },
  { code: 'sw', name: 'Kiswahili', nativeName: 'Kiswahili', flagEmoji: '🇹🇿', direction: 'ltr' },
  { code: 'lg', name: 'Luganda', nativeName: 'Oluganda', flagEmoji: '🇺🇬', direction: 'ltr' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', flagEmoji: '��🇵', direction: 'ltr' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', flagEmoji: '��🇷', direction: 'ltr' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', flagEmoji: '🇮🇳', direction: 'ltr' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', flagEmoji: '🇷🇺', direction: 'ltr' },
];

export class DynamicI18nEngine {
  private static instance: DynamicI18nEngine;
  private currentLocale: string = 'en';
  private localePacks: Map<string, LocalePack> = new Map();
  private changeListeners: Array<(locale: string) => void> = [];

  private constructor() {
    this._seedBundledLocales();
    this._loadPersistedCustomPacks();
    const savedLocale = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY_ACTIVE) : null;
    if (savedLocale && this.localePacks.has(savedLocale)) {
      this.setLocale(savedLocale);
    }
  }

  public static getInstance(): DynamicI18nEngine {
    if (!DynamicI18nEngine.instance) {
      DynamicI18nEngine.instance = new DynamicI18nEngine();
    }
    return DynamicI18nEngine.instance;
  }

  // ─── Seed Bundled Locales ───────────────────────────────────────────────────

  private _seedBundledLocales(): void {
    const baseEnDict: Record<string, string> = {
      'app.title': 'Sovereign Mesh Platform',
      'app.subtitle': 'Decentralized Edge Intelligence & Autonomous Systems',
      'common.save': 'Save Changes',
      'common.cancel': 'Cancel',
      'common.close': 'Close',
      'common.edit': 'Edit',
      'common.delete': 'Delete',
      'common.loading': 'Loading…',
      'common.search': 'Search…',
      'common.status': 'Status',
      'common.active': 'Active',
      'common.installed': 'Installed',
      'common.available': 'Available',
      'auth.signIn': 'Sign In',
      'auth.signOut': 'Sign Out',
      'auth.switchProfile': 'Switch Profile',
      'auth.manageAll': 'Manage All',
      'auth.biometricLogin': 'Biometric Authentication',
      'auth.inviteCode': 'Invite Code',
      'settings.title': 'Control Panel & Settings',
      'settings.language': 'Language & Translations',
      'settings.modules': 'Modules & Collaboration',
      'settings.general': 'General Settings',
      'header.profile_menu': 'Profile & Multi-Account Menu',
      'header.settings': 'Settings & Control Panel',
      'header.architect': 'AI System Architect',
      'header.voice': 'Voice AI',
      'header.genui': 'Generative UI Canvas',
      'modules.hotload.title': 'Hot-Load & Install New Sovereign Module',
      'modules.hotload.dropzone': 'Drop .json manifest, .zip or .tar.gz bundle here, or click to select',
      'modules.hotload.urlPlaceholder': 'https://modules.sovereign.app/my-module/manifest.json',
      'modules.hotload.install': 'Install Module',
      'modules.hotload.verifying': 'Verifying…',
      'modules.hotload.success': 'Module installed successfully',
      'modules.hotload.error': 'Installation failed',
      'i18n.managerTitle': 'Language & Translation Manager',
      'i18n.downloadBase': 'Download Base File (en.json)',
      'i18n.downloadTemplate': 'Download Template',
      'i18n.installPack': 'Install Language Pack',
      'i18n.coverage': 'Key coverage',
      'i18n.activate': 'Set Active',
      'i18n.remove': 'Remove Pack',
      'i18n.rtl': 'RTL Layout',
      'i18n.uploadPlaceholder': 'Upload translation .json dictionary',
    };

    this.localePacks.set('en', {
      locale: 'en',
      name: 'English (US)',
      nativeName: 'English (US)',
      flagEmoji: '🇺🇸',
      direction: 'ltr',
      isCustom: false,
      dictionary: baseEnDict,
    });

    this.localePacks.set('es', {
      locale: 'es',
      name: 'Spanish',
      nativeName: 'Español',
      flagEmoji: '🇪🇸',
      direction: 'ltr',
      isCustom: false,
      dictionary: {
        'app.title': 'Plataforma Sovereign Mesh',
        'app.subtitle': 'Inteligencia de Borde Descentralizada y Sistemas Autónomos',
        'common.save': 'Guardar Cambios',
        'common.cancel': 'Cancelar',
        'common.close': 'Cerrar',
        'common.edit': 'Editar',
        'common.delete': 'Eliminar',
        'common.loading': 'Cargando…',
        'common.search': 'Buscar…',
        'auth.signIn': 'Iniciar Sesión',
        'auth.signOut': 'Cerrar Sesión',
        'auth.switchProfile': 'Cambiar Perfil',
        'settings.title': 'Panel de Control y Configuración',
        'settings.language': 'Idioma y Traducciones',
        'i18n.managerTitle': 'Gestor de Idiomas y Traducciones',
        'i18n.downloadBase': 'Descargar Archivo Base (en.json)',
        'i18n.activate': 'Activar',
      },
    });

    this.localePacks.set('fr', {
      locale: 'fr',
      name: 'French',
      nativeName: 'Français',
      flagEmoji: '🇫🇷',
      direction: 'ltr',
      isCustom: false,
      dictionary: {
        'app.title': 'Plateforme Sovereign Mesh',
        'common.save': 'Enregistrer',
        'common.cancel': 'Annuler',
        'common.close': 'Fermer',
        'auth.signIn': 'Se Connecter',
        'auth.signOut': 'Se Déconnecter',
        'settings.title': 'Paramètres',
        'settings.language': 'Langue et Traductions',
      },
    });

    this.localePacks.set('ar', {
      locale: 'ar',
      name: 'Arabic',
      nativeName: 'العربية',
      flagEmoji: '🇸🇦',
      direction: 'rtl',
      isCustom: false,
      dictionary: {
        'app.title': 'منصة السيادة الرقمية',
        'app.subtitle': 'ذكاء الحافة اللامركزي والأنظمة الذاتية',
        'common.save': 'حفظ التغييرات',
        'common.cancel': 'إلغاء',
        'common.close': 'إغلاق',
        'common.edit': 'تعديل',
        'auth.signIn': 'تسجيل الدخول',
        'auth.signOut': 'تسجيل الخروج',
        'settings.title': 'لوحة التحكم والإعدادات',
        'settings.language': 'اللغة والترجمات',
      },
    });

    this.localePacks.set('zh', {
      locale: 'zh',
      name: 'Chinese (Simplified)',
      nativeName: '简体中文',
      flagEmoji: '🇨🇳',
      direction: 'ltr',
      isCustom: false,
      dictionary: {
        'app.title': '主权网状平台',
        'common.save': '保存更改',
        'common.cancel': '取消',
        'common.close': '关闭',
        'auth.signIn': '登录',
        'auth.signOut': '退出登录',
        'settings.title': '控制面板与设置',
        'settings.language': '语言与翻译',
      },
    });
  }

  // ─── Persistence ────────────────────────────────────────────────────────────

  private _loadPersistedCustomPacks(): void {
    try {
      if (typeof localStorage === 'undefined') return;
      const raw = localStorage.getItem(STORAGE_KEY_PACKS);
      if (!raw) return;
      const packs: LocalePack[] = JSON.parse(raw);
      packs.forEach((p) => this.localePacks.set(p.locale, { ...p, isCustom: true }));
    } catch {
      console.warn('[DynamicI18nEngine] Failed to load persisted custom packs.');
    }
  }

  private _persistCustomPacks(): void {
    try {
      if (typeof localStorage === 'undefined') return;
      const custom = Array.from(this.localePacks.values()).filter((p) => p.isCustom);
      localStorage.setItem(STORAGE_KEY_PACKS, JSON.stringify(custom));
    } catch {
      console.warn('[DynamicI18nEngine] Failed to persist custom packs.');
    }
  }

  // ─── Core Translation & Locale API ──────────────────────────────────────────

  public getLocale(): string {
    return this.currentLocale;
  }

  public getCurrentLocale(): string {
    return this.getLocale();
  }

  public getAllLocales(): LocalePackMeta[] {
    const basePack = this.localePacks.get('en');
    const baseKeyCount = basePack ? Object.keys(basePack.dictionary).length : 1;

    return Array.from(this.localePacks.values()).map((p) => {
      const keyCount = Object.keys(p.dictionary).length;
      const coveragePct = p.locale === 'en' ? 100 : Math.min(100, Math.round((keyCount / baseKeyCount) * 100));
      return {
        locale: p.locale,
        name: p.name,
        direction: p.direction,
        isCustom: !!p.isCustom,
        keyCount,
        coveragePct,
      };
    });
  }

  public mergeLocalePack(pack: LocalePack): LocalePackMeta {
    return this.registerLanguagePack(pack);
  }

  public registerLanguagePack(pack: LocalePack): LocalePackMeta {
    const existing = this.localePacks.get(pack.locale);
    const merged: LocalePack = {
      ...pack,
      isCustom: true,
      dictionary: { ...(existing?.dictionary ?? {}), ...pack.dictionary },
    };
    this.localePacks.set(pack.locale, merged);
    this._persistCustomPacks();

    const basePack = this.localePacks.get('en');
    const baseKeyCount = basePack ? Object.keys(basePack.dictionary).length : 1;
    const customKeyCount = Object.keys(merged.dictionary).length;
    const coveragePct = baseKeyCount > 0 ? Math.round((customKeyCount / baseKeyCount) * 100) : 100;

    return {
      locale: merged.locale,
      name: merged.name,
      direction: merged.direction,
      isCustom: true,
      keyCount: customKeyCount,
      coveragePct,
    };
  }

  public removeCustomPack(locale: string): boolean {
    const pack = this.localePacks.get(locale);
    if (!pack?.isCustom) return false;
    this.localePacks.delete(locale);
    if (this.currentLocale === locale) this.setLocale('en');
    this._persistCustomPacks();
    return true;
  }

  public setLocale(locale: string): boolean {
    const pack = this.localePacks.get(locale);
    if (!pack) return false;
    this.currentLocale = locale;
    if (typeof document !== 'undefined') {
      document.documentElement.dir = pack.direction;
      document.documentElement.lang = locale;
    }
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(STORAGE_KEY_ACTIVE, locale);
      }
    } catch {}
    this.changeListeners.forEach((l) => l(locale));
    return true;
  }

  public subscribe(listener: (locale: string) => void): () => void {
    this.changeListeners.push(listener);
    return () => {
      this.changeListeners = this.changeListeners.filter((l) => l !== listener);
    };
  }

  public t(
    key: string,
    varsOrFallback?: Record<string, string | number> | string,
    fallback?: string
  ): string {
    const vars = typeof varsOrFallback === 'object' && varsOrFallback !== null ? varsOrFallback : undefined;
    const resolvedFallback = typeof varsOrFallback === 'string' ? varsOrFallback : fallback;

    const activePack = this.localePacks.get(this.currentLocale);
    if (activePack && activePack.dictionary[key]) {
      return interpolate(activePack.dictionary[key], vars);
    }
    const basePack = this.localePacks.get('en');
    if (basePack && basePack.dictionary[key]) {
      return interpolate(basePack.dictionary[key], vars);
    }
    return resolvedFallback !== undefined ? resolvedFallback : key;
  }

  public translate(
    key: string,
    varsOrFallback?: Record<string, string | number> | string,
    fallback?: string
  ): string {
    return this.t(key, varsOrFallback, fallback);
  }

  public isRtl(): boolean {
    const activePack = this.localePacks.get(this.currentLocale);
    return activePack?.direction === 'rtl';
  }

  // ─── Language Catalog Discovery ─────────────────────────────────────────────

  public getInstalledLanguages(): LanguageCatalogEntry[] {
    const basePack = this.localePacks.get('en');
    const baseKeyCount = basePack ? Object.keys(basePack.dictionary).length : 1;

    return Array.from(this.localePacks.values()).map((p) => {
      const catalogDef = GLOBAL_LANGUAGE_CATALOG.find((c) => c.code === p.locale);
      const keyCount = Object.keys(p.dictionary).length;
      const coveragePct = p.locale === 'en' ? 100 : Math.min(100, Math.round((keyCount / baseKeyCount) * 100));

      return {
        code: p.locale,
        name: p.name || catalogDef?.name || p.locale,
        nativeName: p.nativeName || catalogDef?.nativeName || p.name,
        flagEmoji: p.flagEmoji || catalogDef?.flagEmoji || '🌐',
        direction: p.direction,
        isInstalled: true,
        isCustom: !!p.isCustom,
        isActive: p.locale === this.currentLocale,
        keyCount,
        coveragePct,
      };
    });
  }

  public getAvailableCatalog(): LanguageCatalogEntry[] {
    const installedCodes = new Set(this.localePacks.keys());

    return GLOBAL_LANGUAGE_CATALOG.filter((c) => !installedCodes.has(c.code)).map((c) => ({
      code: c.code,
      name: c.name,
      nativeName: c.nativeName,
      flagEmoji: c.flagEmoji,
      direction: c.direction,
      isInstalled: false,
      isCustom: false,
      isActive: false,
      keyCount: 0,
      coveragePct: 0,
    }));
  }

  // ─── Base File Download & Template Generation ───────────────────────────────

  public exportBaseDictionary(locale: string = 'en'): TranslationFileExport {
    const pack = this.localePacks.get(locale) || this.localePacks.get('en')!;
    const content = JSON.stringify(pack.dictionary, null, 2);
    return {
      fileName: `${locale}.json`,
      contentType: 'application/json',
      content,
    };
  }

  public generateTranslationTemplate(targetLocale: string): TranslationFileExport {
    const basePack = this.localePacks.get('en')!;
    const templateDict: Record<string, string> = {};

    for (const key of Object.keys(basePack.dictionary)) {
      templateDict[key] = ''; // Empty for translator fill-in
    }

    return {
      fileName: `${targetLocale}.template.json`,
      contentType: 'application/json',
      content: JSON.stringify(templateDict, null, 2),
    };
  }

  // ─── Validation & Key Parity Analysis ───────────────────────────────────────

  public validateTranslationPayload(
    targetLocale: string,
    rawPayload: string | Record<string, any>
  ): TranslationValidationReport {
    const errors: string[] = [];
    let parsed: Record<string, any>;

    if (typeof rawPayload === 'string') {
      try {
        parsed = JSON.parse(rawPayload);
      } catch (err: any) {
        return {
          valid: false,
          targetLocale,
          totalBaseKeys: 0,
          translatedKeys: 0,
          missingKeys: [],
          extraKeys: [],
          coveragePct: 0,
          errors: [`Invalid JSON Syntax: ${err?.message || 'Parse error'}`],
        };
      }
    } else {
      parsed = rawPayload;
    }

    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return {
        valid: false,
        targetLocale,
        totalBaseKeys: 0,
        translatedKeys: 0,
        missingKeys: [],
        extraKeys: [],
        coveragePct: 0,
        errors: ['Translation root must be a JSON key-value object.'],
      };
    }

    // Flatten nested objects if any
    const flattened: Record<string, string> = {};
    const flattenObject = (obj: Record<string, any>, prefix = '') => {
      for (const [k, v] of Object.entries(obj)) {
        const fullKey = prefix ? `${prefix}.${k}` : k;
        if (typeof v === 'object' && v !== null && !Array.isArray(v)) {
          flattenObject(v, fullKey);
        } else if (typeof v === 'string') {
          flattened[fullKey] = v;
        } else {
          flattened[fullKey] = String(v);
        }
      }
    };
    flattenObject(parsed);

    const basePack = this.localePacks.get('en')!;
    const baseKeys = Object.keys(basePack.dictionary);
    const uploadedKeys = new Set(Object.keys(flattened));

    const missingKeys: string[] = [];
    let translatedCount = 0;

    for (const bk of baseKeys) {
      if (!uploadedKeys.has(bk) || flattened[bk].trim() === '') {
        missingKeys.push(bk);
      } else {
        translatedCount++;
      }
    }

    const extraKeys: string[] = [];
    for (const uk of uploadedKeys) {
      if (!basePack.dictionary[uk]) {
        extraKeys.push(uk);
      }
    }

    const coveragePct = Math.round((translatedCount / baseKeys.length) * 100);

    return {
      valid: errors.length === 0,
      targetLocale,
      totalBaseKeys: baseKeys.length,
      translatedKeys: translatedCount,
      missingKeys,
      extraKeys,
      coveragePct,
      errors,
    };
  }

  // ─── 2-Column Editor Data Supplier & Updater ────────────────────────────────

  public getTranslationRecords(locale: string): TranslationEditRecord[] {
    const basePack = this.localePacks.get('en')!;
    const targetPack = this.localePacks.get(locale);
    const targetDict = targetPack?.dictionary || {};

    return Object.keys(basePack.dictionary).map((key) => {
      const baseValue = basePack.dictionary[key];
      const currentValue = targetDict[key] || '';
      return {
        key,
        baseValue,
        currentValue,
        isModified: false,
        isMissing: !currentValue || currentValue.trim() === '',
      };
    });
  }

  public async updateTranslations(
    locale: string,
    edits: Record<string, string>,
    adminUserId: string = 'admin_1'
  ): Promise<{ success: boolean; error?: string }> {
    let pack = this.localePacks.get(locale);
    if (!pack) {
      const catalogDef = GLOBAL_LANGUAGE_CATALOG.find((c) => c.code === locale);
      if (!catalogDef) return { success: false, error: `Unsupported locale code: ${locale}` };

      pack = {
        locale,
        name: catalogDef.name,
        nativeName: catalogDef.nativeName,
        flagEmoji: catalogDef.flagEmoji,
        direction: catalogDef.direction,
        dictionary: {},
        isCustom: true,
      };
      this.localePacks.set(locale, pack);
    }

    // Merge edits
    for (const [k, v] of Object.entries(edits)) {
      if (v.trim()) {
        pack.dictionary[k] = v.trim();
      }
    }
    pack.isCustom = true;
    this._persistCustomPacks();

    await auditLogger.logEvent({
      actorId: adminUserId,
      role: 'superadmin',
      event: 'I18N_TRANSLATION_UPDATED',
      intentClass: 'UI_MUTATION',
      toolsUsed: [
        {
          toolName: 'i18n:update_dictionary',
          params: { locale, updatedKeyCount: Object.keys(edits).length },
          executionResult: 'SUCCESS',
        },
      ],
      riskScore: 0.05,
    });

    this.changeListeners.forEach((l) => l(this.currentLocale));
    return { success: true };
  }

  // ─── Install & Upload Actions ───────────────────────────────────────────────

  public async installFromCatalog(
    locale: string,
    adminUserId: string = 'admin_1'
  ): Promise<{ success: boolean; error?: string }> {
    const catalogDef = GLOBAL_LANGUAGE_CATALOG.find((c) => c.code === locale);
    if (!catalogDef) {
      return { success: false, error: `Language ${locale} not found in catalog.` };
    }

    const newPack: LocalePack = {
      locale: catalogDef.code,
      name: catalogDef.name,
      nativeName: catalogDef.nativeName,
      flagEmoji: catalogDef.flagEmoji,
      direction: catalogDef.direction,
      dictionary: {},
      isCustom: true,
    };

    this.localePacks.set(locale, newPack);
    this._persistCustomPacks();

    await auditLogger.logEvent({
      actorId: adminUserId,
      role: 'superadmin',
      event: 'I18N_LANGUAGE_PACK_INSTALLED',
      intentClass: 'UI_MUTATION',
      toolsUsed: [
        {
          toolName: 'i18n:install_pack',
          params: { locale },
          executionResult: 'SUCCESS',
        },
      ],
      riskScore: 0.05,
    });

    return { success: true };
  }

  public async uploadTranslationFile(
    locale: string,
    jsonString: string,
    adminUserId: string = 'admin_1'
  ): Promise<{ success: boolean; report?: TranslationValidationReport; error?: string }> {
    const report = this.validateTranslationPayload(locale, jsonString);
    if (!report.valid) {
      return { success: false, report, error: report.errors.join('; ') };
    }

    const parsed = JSON.parse(jsonString);
    const catalogDef = GLOBAL_LANGUAGE_CATALOG.find((c) => c.code === locale);

    const pack: LocalePack = {
      locale,
      name: catalogDef?.name || locale,
      nativeName: catalogDef?.nativeName || locale,
      flagEmoji: catalogDef?.flagEmoji || '🌐',
      direction: catalogDef?.direction || 'ltr',
      dictionary: parsed,
      isCustom: true,
    };

    this.localePacks.set(locale, pack);
    this._persistCustomPacks();

    await auditLogger.logEvent({
      actorId: adminUserId,
      role: 'superadmin',
      event: 'I18N_DICTIONARY_UPLOADED',
      intentClass: 'UI_MUTATION',
      toolsUsed: [
        {
          toolName: 'i18n:upload_file',
          params: { locale, keyCount: Object.keys(parsed).length, coveragePct: report.coveragePct },
          executionResult: 'SUCCESS',
        },
      ],
      riskScore: 0.05,
    });

    this.changeListeners.forEach((l) => l(this.currentLocale));
    return { success: true, report };
  }

  public async removeLanguagePack(
    locale: string,
    adminUserId: string = 'admin_1'
  ): Promise<boolean> {
    if (locale === 'en') return false; // English baseline cannot be removed

    const deleted = this.localePacks.delete(locale);
    if (deleted) {
      if (this.currentLocale === locale) {
        this.setLocale('en');
      }
      this._persistCustomPacks();

      await auditLogger.logEvent({
        actorId: adminUserId,
        role: 'superadmin',
        event: 'I18N_LANGUAGE_PACK_REMOVED',
        intentClass: 'UI_MUTATION',
        toolsUsed: [
          {
            toolName: 'i18n:remove_pack',
            params: { locale },
            executionResult: 'SUCCESS',
          },
        ],
        riskScore: 0.05,
      });
    }
    return deleted;
  }
}

export const dynamicI18nEngine = DynamicI18nEngine.getInstance();
